// Base URL for all backend API requests.
// export const BASE = "http://localhost:8000/api";  // For Localhost use
export const BASE = "/api";  // To be used in production

// Retrieve the stored auth token from local or session storage.
export const getToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token");

function authHeaders() {
  const tok = getToken();
  return {
    "Content-Type": "application/json",
    ...(tok ? { "Authorization": `Bearer ${tok}` } : {}),
  };
}

async function handle(fetchPromise) {
  const res         = await fetchPromise;
  const contentType = res.headers.get("content-type") ?? "";
  const data        = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail ?? data ?? res.statusText;
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return data;
}

// ── Auth ───────────────────────────────────────────────────────────────────────
export const register = (name, email, password) =>
  handle(fetch(`${BASE}/auth/register`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, password }),
  }));

export const login = (email, password) =>
  handle(fetch(`${BASE}/auth/login`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  }));

export const getMe = () =>
  handle(fetch(`${BASE}/auth/me`, { headers: authHeaders() }));

export const changePassword = (current_password, new_password) =>
  handle(fetch(`${BASE}/auth/change-password`, {
    method: "POST", headers: authHeaders(),
    body: JSON.stringify({ current_password, new_password }),
  }));

export const forgotPassword = (email) =>
  handle(fetch(`${BASE}/auth/forgot-password`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  }));

export const resetPasswordViaToken = (token, new_password) =>
  handle(fetch(`${BASE}/auth/reset-password`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password }),
  }));

// ── Wizard & Search — PUBLIC (no token required) ───────────────────────────────
export const fetchWizardOptions = () =>
  handle(fetch(`${BASE}/schemes/wizard-options`));

export const searchSchemes = ({ actor_id, crop_id, objective_id, complexity }) =>
  handle(fetch(
    `${BASE}/schemes/search?actor_id=${actor_id}&crop_id=${crop_id}&objective_id=${objective_id}&complexity=${complexity}`
  ));

// ── Catalog — requires risk_manager or admin token ─────────────────────────────
export const fetchCatalog = ({ crop_id = "", actor_id = "", objective_id = "", complexity = "" } = {}) => {
  const p = new URLSearchParams();
  if (crop_id)      p.set("crop_id",      crop_id);
  if (actor_id)     p.set("actor_id",     actor_id);
  if (objective_id) p.set("objective_id", objective_id);
  if (complexity)   p.set("complexity",   complexity);
  return handle(fetch(`${BASE}/schemes/catalog?${p}`, { headers: authHeaders() }));
};

// ── Documents — PUBLIC downloads ───────────────────────────────────────────────
export const getDocDownloadUrl = (docId) => `${BASE}/schemes/documents/${docId}/download`;
export const getDocPreviewUrl  = (docId) => `${BASE}/schemes/documents/${docId}/preview`;

// ── Admin: Excel upload ────────────────────────────────────────────────────────
export const uploadExcel = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return handle(fetch(`${BASE}/schemes/upload-excel`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${getToken()}` },
    body: fd,
  }));
};

// ── Admin: Scheme management ───────────────────────────────────────────────────
export const listSchemes = () =>
  handle(fetch(`${BASE}/schemes/`, { headers: authHeaders() }));

export const updateScore = (schemeId, score, expert_comment = "") =>
  handle(fetch(`${BASE}/schemes/${schemeId}/score`, {
    method: "PUT", headers: authHeaders(),
    body: JSON.stringify({ score, expert_comment }),
  }));

export const deleteScheme = (schemeId) =>
  handle(fetch(`${BASE}/schemes/${schemeId}`, { method: "DELETE", headers: authHeaders() }));

// ── Admin: Document attachment ─────────────────────────────────────────────────
export const attachDocument = (file, schemeId, optionText = "", optionMeasure = "", title = "") => {
  const fd = new FormData();
  fd.append("file",           file);
  fd.append("scheme_id",      schemeId);
  fd.append("option_text",    optionText);
  fd.append("option_measure", optionMeasure);
  fd.append("title",          title);
  return handle(fetch(`${BASE}/schemes/documents/upload`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${getToken()}` },
    body: fd,
  }));
};

export const deleteAttachedDoc = (docId) =>
  handle(fetch(`${BASE}/schemes/documents/${docId}`, { method: "DELETE", headers: authHeaders() }));

// ── Admin: Users ───────────────────────────────────────────────────────────────
export const adminGetUsers      = () => handle(fetch(`${BASE}/admin/users`, { headers: authHeaders() }));
export const adminSetStatus     = (userId, status) =>
  handle(fetch(`${BASE}/admin/users/${userId}/status`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify({ status }),
  }));
export const adminDeleteUser    = (userId) =>
  handle(fetch(`${BASE}/admin/users/${userId}`, { method: "DELETE", headers: authHeaders() }));
export const adminGetPrivileges = (userId) =>
  handle(fetch(`${BASE}/admin/users/${userId}/privileges`, { headers: authHeaders() }));
export const adminSetPrivileges = (userId, data) =>
  handle(fetch(`${BASE}/admin/users/${userId}/privileges`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify(data),
  }));
export const adminResetPassword = (userId, new_password) =>
  handle(fetch(`${BASE}/auth/admin-reset/${userId}`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify({ new_password }),
  }));
export const adminUpdateRole    = (userId, role) =>
  handle(fetch(`${BASE}/auth/users/${userId}/role`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify({ role }),
  }));
export const adminUpdateProfile = (userId, name, email) =>
  handle(fetch(`${BASE}/auth/users/${userId}/profile`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify({ name, email }),
  }));
export const adminGetLogs       = (limit = 100, action = "") =>
  handle(fetch(`${BASE}/admin/logs?limit=${limit}${action ? `&action=${action}` : ""}`,
    { headers: authHeaders() }));
export const adminGetStats      = () =>
  handle(fetch(`${BASE}/admin/stats`, { headers: authHeaders() }));

// ── Admin: document list ───────────────────────────────────────────────────────
export const listAllDocuments = () =>
  handle(fetch(`${BASE}/schemes/documents`, { headers: authHeaders() }));
